import React, { useState, useRef, useEffect } from 'react';
import { Settings, Moon, Sun, LogOut } from 'lucide-react';
 
const UserDock = ({
  userName,
  userPicture,
  isDarkMode,
  colors,
  toggleTheme,
  onSettings,
  onLogout,
  poweredByLogo,
  brandLogo,
  bottomOffset = '4px',
}) => {
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const dockRef = useRef(null);
  const expanded = pinned || hovered;
 
  // Collapse a pinned dock when clicking elsewhere on the page
  useEffect(() => {
    if (!pinned) return;
    const onDocClick = (e) => {
      if (dockRef.current && !dockRef.current.contains(e.target)) setPinned(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [pinned]);
 
  const iconBtn = (extra = {}) => ({
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'color 0.2s ease, background-color 0.2s ease',
    ...extra,
  });
 
  const firstName = (userName || '').trim().split(' ')[0] || '';
 
  return (
    <div
      ref={dockRef}
      className="dq-userdock"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'fixed',
        bottom: `calc(${bottomOffset} + env(safe-area-inset-bottom, 0px))`,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px 4px 5px',
        backgroundColor: colors.nodeBg,
        borderRadius: '24px',
        border: `1px solid ${colors.nodeBorder}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 10,
        boxShadow: expanded
          ? (isDarkMode ? '0 4px 18px rgba(0,0,0,0.4)' : '0 4px 18px rgba(0,0,0,0.1)')
          : 'none',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      {/* Avatar — always visible, toggles pin on click */}
      {/* DataQuad brand mark — lives in the dock now instead of floating top-left */}
      {brandLogo && (
        <img
          src={brandLogo}
          alt="DataQuad"
          style={{
            width: '22px',
            height: '22px',
            objectFit: 'contain',
            borderRadius: '6px',
            marginRight: '2px',
            flexShrink: 0,
          }}
        />
      )}
      <button
        type="button"
        onClick={() => setPinned(p => !p)}
        title={userName || 'Account'}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse account controls' : 'Expand account controls'}
        style={{
          width: '26px',
          height: '26px',
          padding: 0,
          borderRadius: '50%',
          border: expanded ? `1.5px solid ${isDarkMode ? '#5a9cf5' : '#2563EB'}` : '1.5px solid transparent',
          background: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'border-color 0.25s ease, transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
          transform: expanded ? 'scale(1.05)' : 'scale(1)',
        }}
      >
        {userPicture ? (
          <img
            src={userPicture}
            alt={userName || 'Profile'}
            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563EB 0%, #0EA5E9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
            {(userName || '?').charAt(0).toUpperCase()}
          </span>
        )}
      </button>
 
      {/* Expandable group — name + controls */}
      <div
        aria-hidden={!expanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          maxWidth: expanded ? '240px' : '0px',
          opacity: expanded ? 1 : 0,
          overflow: 'hidden',
          pointerEvents: expanded ? 'auto' : 'none',
          transform: expanded ? 'translateX(0)' : 'translateX(-6px)',
          transition: [
            'max-width 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
            'opacity 0.25s ease',
            'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
          ].join(', '),
        }}
      >
        {firstName && (
          <span style={{
            fontSize: '11.5px',
            fontWeight: 600,
            color: colors.text || colors.textSecondary,
            fontFamily: "'Outfit', -apple-system, sans-serif",
            whiteSpace: 'nowrap',
            maxWidth: '90px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            padding: '0 4px 0 2px',
            flexShrink: 0,
          }}>
            {firstName}
          </span>
        )}
 
        <button
          type="button"
          onClick={onSettings}
          title="Account & Subscription"
          className="dq-userdock-btn"
          style={iconBtn()}
        >
          <Settings size={13} />
        </button>
 
        <button
          type="button"
          onClick={toggleTheme}
          title={isDarkMode ? 'Light mode' : 'Dark mode'}
          className="dq-userdock-btn"
          style={iconBtn()}
        >
          {isDarkMode ? <Sun size={13} /> : <Moon size={13} />}
        </button>
 
        <button
          type="button"
          onClick={onLogout}
          title="Log out"
          className="dq-userdock-btn logout"
          style={iconBtn({ color: isDarkMode ? '#f87171' : '#b91c1c' })}
        >
          <LogOut size={13} />
        </button>
      </div>
 
      {/* Divider */}
      <div style={{ width: '1px', height: '14px', backgroundColor: colors.nodeBorder, flexShrink: 0 }} />
 
      {/* Powered by — always visible */}
      <a
        href="https://noblesyntax.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          textDecoration: 'none',
          opacity: 0.6,
          transition: 'opacity 0.2s ease',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
      >
        <span style={{ fontSize: '10px', color: colors.textMuted, whiteSpace: 'nowrap' }}>Powered by</span>
        <img src={poweredByLogo} alt="Noble Syntax" style={{ height: '10px', opacity: 0.7 }} />
      </a>
 
      <style>{`
        .dq-userdock-btn:hover {
          background-color: ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} !important;
        }
        .dq-userdock-btn.logout:hover {
          background-color: ${isDarkMode ? 'rgba(248,113,113,0.12)' : 'rgba(185,28,28,0.08)'} !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .dq-userdock, .dq-userdock * { transition-duration: 0.01ms !important; }
        }
      `}</style>
    </div>
  );
};
 
export default UserDock;